import { defineStore } from "pinia";
import * as Sentry from "@sentry/nuxt";
import type { Profile } from "~/types/profile";

// AuthUser covers both shapes that flow through the store: UserLoginResource
// (from auth/login) which has `id`, and UserProfileResource (from
// users/profile) which doesn't. Phase 0 may unify these on the backend.
type AuthUser = Profile.LoginUser | Profile.MetaData;

interface LoginSuccess {
   status: 200;
   user: AuthUser;
}
interface LoginFailure {
   status: number;
   data: ApiErrorBody | null;
   message: string | null;
   error: true;
}
type LoginResult = LoginSuccess | LoginFailure;

interface PasswordOpResult {
   data?: unknown;
   message?: string;
   status?: number;
   error?: true;
}

export const useAuthStore = defineStore("auth", () => {
   const { logError, logInfo } = useDebugger();
   const { resetUser } = useMixpanel();
   const { $posthog } = useNuxtApp();
   const { removeToast } = useToast();
   const authed = shallowRef<boolean>(false);
   const user = ref<AuthUser | null>(null);
   const token = shallowRef<string | null>(null);
   // >> GETTERS
   const getUser = computed<AuthUser | null>(() => user.value);
   const getAvatar = computed(() => user.value?.image ?? null);
   const getAuthed = computed<boolean>(() => authed.value ?? false);
   const getToken = computed<string | null>(() => token.value);
   const getUserDisciplines = computed<number[]>(
      () => user.value?.school?.disciplines ?? [1],
   );
   const getPilotDisciplines = computed<number[]>(
      () => user.value?.pilot_disciplines ?? [],
   );
   const isGuest = computed<boolean>(() => user.value?.role === "guest");

   const updateUserData = (
      updates: Partial<AuthUser> | AuthUser | null,
      override: boolean = false,
   ): void => {
      if (override) {
         user.value = updates as AuthUser | null;
      } else if (user.value && updates) {
         Object.assign(user.value, updates);
      }
   };
   // >> ACTIONS
   const setAuth = (userData: AuthUser, authToken: string) => {
      authed.value = true;
      token.value = authToken;
      user.value = userData;
   };

   const clearAuth = () => {
      authed.value = false;
      token.value = null;
      user.value = null;
      resetUser(); // Reset Mixpanel user context on logout
      $posthog?.().reset();
   };
   /**
    *
    * @param {password, email} Object
    */
   const login = async (
      formData: FormData | { email: string; password: string },
   ): Promise<LoginResult> => {
      // Opt out of $apiFetch: this is a pre-auth flow. A 401 here means
      // "wrong credentials," not "session expired" — we don't want the
      // plugin's err401 to fire its toast/logout/redirect.
      const apiUrl = useRuntimeConfig().public.apiUrl;
      isLoading.value = true; // Show loading state

      const email = (formData as { email?: string })?.email;

      try {
         // Add breadcrumb for login attempt
         Sentry?.addBreadcrumb({
            message: "Login attempt started",
            category: "auth",
            level: "info",
            data: { email },
         });

         const body = await $fetch<
            { token: string; user: Profile.LoginUser } | {
               data: { token: string; user: Profile.LoginUser };
            }
         >("auth/login", {
            baseURL: apiUrl,
            body: formData,
            method: "POST",
         });

         // Handle either { token, user } or { data: { token, user } }
         const payload =
            "data" in body && body.data ? body.data : (body as {
               token: string;
               user: Profile.LoginUser;
            });
         if (!payload?.token || !payload?.user) {
            throw new Error("Invalid login response structure");
         }

         removeToast("session-expired");
         token.value = payload.token;
         authed.value = true;
         user.value = payload.user;

         // Add success breadcrumb
         Sentry?.addBreadcrumb({
            message: "Login successful",
            category: "auth",
            level: "info",
            data: { userId: payload.user.id, email: payload.user.email },
         });

         // Set user context for future errors
         Sentry?.setUser({
            id: payload.user.id?.toString(),
            email: payload.user.email,
            username: payload.user.name || payload.user.email,
         });

         // set the token as a cookie for server-side rendering
         await $fetch("/api/auth", { params: { token: payload.token } });

         // if user has not completed PD_Course_onboarding
         if (!user.value?.is_pd_course_completed) {
            const { queryOnboardingCourses } = useOnboardingCourses();
            try {
               await queryOnboardingCourses();
            } catch {
               // non-blocking: a failed onboarding-courses fetch shouldn't fail login
            }
         }

         return { status: 200, user: user.value };
      } catch (err: unknown) {
         const e = err as {
            statusCode?: number;
            status?: number;
            data?: ApiErrorBody;
            response?: { status?: number; _data?: ApiErrorBody };
         };
         const status = e?.statusCode || e?.response?.status || e?.status || 422;
         const responseData = e?.data || e?.response?._data || null;
         const message =
            responseData?.message || responseData?.error?.message || null;

         // 401 is a wrong email/password, not an app error
         if (status !== 401) {
            Sentry?.captureException(err, {
               tags: { error_type: "auth_error", auth_action: "login" },
               extra: {
                  userEmail: email,
                  statusCode: status,
                  responseData,
                  message,
                  endpoint: "auth/login",
               },
            });
         }

         logError("--LOGIN ERROR:", status, err, message, responseData);

         return { status, data: responseData, message, error: true };
      } finally {
         isLoading.value = false; // Always clear loading
      }
   };

   /**
    *
    * @param {email} String
    */

   const checkUserCreation = async (email: string): Promise<boolean> => {
      // Opt out of useApiFetch: pre-auth flow, see login() for reasoning.
      const apiUrl = useRuntimeConfig().public.apiUrl;
      try {
         const data = await $fetch<{ has_completed_setup: boolean }>(
            "password/creation/check",
            { baseURL: apiUrl, params: { email } },
         );
         return data?.has_completed_setup ?? false;
      } catch (err) {
         logError("checkUserCreation failed", err);
         return false;
      }
   };

   /**
    *
    * @param {email} String
    */
   const getDisciplinesByEmail = async (
      email: string,
   ): Promise<number[] | null> => {
      // Opt out of useApiFetch: pre-auth flow, see login() for reasoning.
      const apiUrl = useRuntimeConfig().public.apiUrl;
      try {
         const data = await $fetch<number[]>(
            "password/creation/disciplines",
            { baseURL: apiUrl, params: { email } },
         );
         return data;
      } catch (err) {
         logError("getDisciplinesByEmail failed", err);
         return null;
      }
   };
   /**
    *
    * @param {formData} FormData
    */
   const createPassword = async (
      formData: FormData,
   ): Promise<{ school?: string } | { error: true; message?: string }> => {
      // Opt out of $apiFetch: pre-auth flow, see login() for reasoning.
      const apiUrl = useRuntimeConfig().public.apiUrl;
      try {
         const response = await $fetch<
            { token: string; user: Profile.LoginUser }
         >("password/creation", {
            baseURL: apiUrl,
            body: formData,
            method: "POST",
         });

         logInfo("createPassword:", response);
         token.value = response.token;
         authed.value = true;
         user.value = response.user;

         // set the token as a cookie for server-side rendering
         await $fetch("/api/auth", { params: { token: token.value } });
         return { school: response.user?.school?.slug };
      } catch (error: unknown) {
         logError("createPassword:", error);
         const e = error as { data?: ApiErrorBody };
         return {
            error: true,
            message:
               typeof e?.data?.error === "string"
                  ? e.data.error
                  : e?.data?.error?.message,
         };
      }
   };

   /**
    *
    * @param null
    */
   const logout = async (redirect: boolean = false) => {
      // logout() can be invoked from contexts where Nuxt's async-local-storage
      // has lost track of the current instance (e.g. from $apiFetch's
      // onResponseError → catcher → err401 chain, where ofetch internals break
      // the AsyncLocalStorage continuity). tryUseNuxtApp returns null in that
      // case instead of throwing, so we can degrade gracefully.
      const nuxtApp = tryUseNuxtApp();
      isLoggingOut.value = true;

      try {
         // Raw $fetch to a Nitro server route (not the Laravel API): clears
         // the session cookie. Local logout proceeds even if this fails.
         await $fetch("/api/logout", { method: "POST" });
      } catch (error) {
         logInfo("API logout failed, proceeding with local logout", error);
      } finally {
         if (nuxtApp) {
            await nuxtApp.runWithContext(async () => {
               clearAuth();
               useCookie("session").value = null;
               isLoggingOut.value = false;

               if (redirect) {
                  const route = useRoute();
                  const redirectPath =
                     route.path !== "/login" ? route.path : "/";
                  await navigateTo({
                     path: "/login",
                     query: { redirect: redirectPath },
                  });
               }
            });
         } else {
            // No Nuxt context: do best-effort in-memory cleanup. The next
            // navigation will redirect via middleware, and the cookie will be
            // cleared either by piniaPersist's reaction to authed=false or
            // by the next page's auth bootstrap.
            clearAuth();
            isLoggingOut.value = false;
         }
      }
   };
   /**
    *
    * @param null
    */
   const forgotPassword = async (
      formData: FormData | { email: string },
   ): Promise<PasswordOpResult> => {
      // Opt out of $apiFetch: pre-auth flow, see login() for reasoning.
      const apiUrl = useRuntimeConfig().public.apiUrl;
      const email = (formData as { email?: string })?.email;
      try {
         Sentry?.addBreadcrumb({
            message: "Forgot password request started",
            category: "auth",
            level: "info",
            data: { email },
         });

         const response = await $fetch<{ message?: string }>(
            "auth/password/forgot",
            { baseURL: apiUrl, body: formData, method: "POST" },
         );

         Sentry?.addBreadcrumb({
            message: "Forgot password request successful",
            category: "auth",
            level: "info",
         });

         return {
            data: response,
            message: response?.message || "Password reset email sent",
            status: 200,
         };
      } catch (err: unknown) {
         const e = err as {
            statusCode?: number;
            data?: ApiErrorBody;
            message?: string;
         };
         const status = e?.statusCode || 422;
         const responseData = e?.data?.error || e?.data || null;
         const message =
            (typeof responseData === "object" && responseData?.message) ||
            e?.message ||
            "Password reset failed";

         Sentry?.captureException(err, {
            tags: { error_type: "auth_error", auth_action: "forgot_password" },
            extra: { userEmail: email, statusCode: status, responseData },
         });
         return { error: true, message };
      }
   };
   const resetPassword = async (
      formData: FormData,
   ): Promise<PasswordOpResult> => {
      // Opt out of $apiFetch: pre-auth flow, see login() for reasoning.
      const apiUrl = useRuntimeConfig().public.apiUrl;
      try {
         Sentry?.addBreadcrumb({
            message: "Password reset request started",
            category: "auth",
            level: "info",
         });

         const response = await $fetch<{ message?: string }>(
            "auth/password/reset",
            { baseURL: apiUrl, body: formData, method: "POST" },
         );

         Sentry?.addBreadcrumb({
            message: "Password reset successful",
            category: "auth",
            level: "info",
         });

         logInfo("resetPassword:", 200, response);
         return {
            data: response,
            message: response?.message || "Password reset successful",
            status: 200,
         };
      } catch (err: unknown) {
         const e = err as {
            statusCode?: number;
            status?: number;
            data?: ApiErrorBody;
            response?: { status?: number; _data?: ApiErrorBody };
            message?: string;
         };
         const status =
            e?.statusCode || e?.response?.status || e?.status || 422;
         const responseData = e?.data || e?.response?._data || null;
         const message =
            responseData?.message || e?.message || "Password reset failed";

         Sentry?.captureException(err, {
            tags: { error_type: "auth_error", auth_action: "reset_password" },
            extra: { statusCode: status, responseData },
         });

         logError("ERROR:", status, message, responseData);
         return { status, data: responseData, message, error: true };
      }
   };

   // Add loading states directly to the store
   const isLoading = ref(false);
   const isLoggingOut = ref(false);

   return {
      authed,
      token,
      user,
      isGuest,
      getUser,
      getAvatar,
      getAuthed,
      getToken,
      getUserDisciplines,
      getPilotDisciplines,
      updateUserData,
      login,
      logout,
      forgotPassword,
      getDisciplinesByEmail,
      createPassword,
      checkUserCreation,
      resetPassword,
      setAuth,
      clearAuth,
      // getProfile,
      // Computed (not readonly refs) so Pinia treats these as getters and skips hydrating them.
      isLoading: computed(() => isLoading.value),
      isLoggingOut: computed(() => isLoggingOut.value),
   };
});
